import { useState, useId } from 'react';
import api from '../API.js';
import { useAuth } from '../providers/AuthContext';
import loginStyles from './Login.module.css';
import ssoStyles from './LoginSsoFirst.module.css';

/**
 * One-time email sign-in block (girder-emailauth). Used from Login and LoginSsoFirst.
 * @param {{ variant?: 'login' | 'ssoFirst' }} props
 */
export default function EmailAuthLoginSection({ variant = 'login' }) {
    const idPrefix = useId();
    const { emailAuth } = useAuth();
    const [email, setEmail] = useState('');
    const [pending, setPending] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const isSso = variant === 'ssoFirst';

    if (!emailAuth?.oneTimeEmailLogin) {
        return null;
    }

    const hint = (emailAuth.emailFromHint || '').trim();

    async function submit(e) {
        e.preventDefault();
        setPending(true);
        setMessage('');
        setError('');
        try {
            const resp = await api.post('emailauth/auth/link', {
                json: {
                    email,
                    redirect: window.location.href,
                },
            });
            if (resp.status !== 200) {
                setError(
                    resp.status === 503
                        ? 'Email sign-in is not available right now. Contact an administrator.'
                        : 'Could not send sign-in email. Try again later or contact an administrator.',
                );
                return;
            }
            setMessage(
                'If an account exists for that email, a one-time link has been sent.',
            );
        } catch {
            setError(
                'Could not send sign-in email. Try again later or contact an administrator.',
            );
        } finally {
            setPending(false);
        }
    }

    const formInner = (
        <>
            <p
                className={isSso ? ssoStyles.primaryText : undefined}
                style={
                    isSso
                        ? { marginBottom: 8, fontSize: '0.9rem', opacity: 0.9 }
                        : { margin: '0 0 10px', fontSize: '0.9rem', opacity: 0.9 }
                }
            >
                A user account with this email must already exist in the system (registered
                previously or created by an administrator).
            </p>
            {hint ? (
                <p
                    style={
                        isSso
                            ? { marginBottom: 8, fontSize: '0.85rem', opacity: 0.85 }
                            : { margin: '0 0 10px', fontSize: '0.85rem', opacity: 0.85 }
                    }
                >
                    Messages are sent from: {hint}
                </p>
            ) : null}
            <form onSubmit={submit} aria-busy={pending}>
                <div className={isSso ? undefined : loginStyles.loginInputs}>
                    <label
                        htmlFor={`${idPrefix}-emailauth-email`}
                        style={isSso ? { display: 'block', marginBottom: 6 } : undefined}
                    >
                        Email:
                    </label>
                    <input
                        type="email"
                        id={`${idPrefix}-emailauth-email`}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@example.org"
                        required
                        autoComplete="email"
                        style={
                            isSso
                                ? {
                                      width: '100%',
                                      padding: '10px 12px',
                                      borderRadius: 10,
                                      border: '1px solid rgba(0,0,0,0.2)',
                                  }
                                : undefined
                        }
                    />
                </div>
                <button
                    type="submit"
                    className={isSso ? ssoStyles.primaryButton : loginStyles.submitButton}
                    disabled={pending}
                    style={isSso ? { marginTop: 10, width: '100%' } : undefined}
                >
                    {pending ? 'Sending…' : 'Send me a one-time link'}
                </button>
                {message ? (
                    <div style={{ marginTop: 10 }} aria-live="polite">
                        {message}
                    </div>
                ) : null}
                {error ? (
                    <div style={{ marginTop: 10 }} className={loginStyles.loginFailed} role="alert">
                        {error}
                    </div>
                ) : null}
            </form>
            <p
                style={
                    isSso
                        ? { marginTop: 10, fontSize: '0.85rem', opacity: 0.85 }
                        : { margin: '10px 0 0', fontSize: '0.85rem', opacity: 0.85 }
                }
            >
                If you belive you should have an account under this email address but do not receive an email within a few minutes, contact an administrator.
            </p>
        </>
    );

    if (isSso) {
        return (
            <div style={{ marginTop: 14 }}>
                <div className={ssoStyles.primaryText} style={{ marginBottom: 8 }}>
                    Or, receive a one-time sign-in link by email.
                </div>
                {formInner}
            </div>
        );
    }

    return (
        <>
            <hr />
            <div className={loginStyles.oauthHeader}>Or, email a one-time sign-in link:</div>
            {formInner}
        </>
    );
}
